import { Link } from '@tanstack/react-router'
import { useState } from 'react'

import { ApiFailure } from '../../app/errors'
import type { ProjectImport, ProjectImportLink } from '../../app/project-imports'
import { useProjectImportByProject, useUnlinkProjectImport } from '../../app/queries'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { Tag } from '../../components/tag'
import { T } from '../../copy/text'
import { t } from '../../copy'

/**
 * 项目页上的「从现成项目接进来的」那张卡。
 *
 * 它要在一屏里回答五件事：接到哪一步了、差在哪儿、这些说法哪来的、
 * 谁批的、以及**怎么撤**。撤销放在这里而不是只放在核对页，是因为
 * 用户想反悔的那一刻，他人在项目页上看着那台不该在这儿的机器。
 *
 * 没有导入草稿的项目整张卡不出现——绝大多数项目不是接进来的，
 * 给它们留一张写着「没有」的卡是白占一屏。
 */
export function ProjectImportCard({
  projectId,
  surface = 'paper',
}: {
  projectId: string
  surface?: 'paper' | 'lulu'
}) {
  const query = useProjectImportByProject(projectId)
  if (query.isPending) return null
  if (query.error) {
    const status = query.error instanceof ApiFailure ? query.error.status : undefined
    if (status === 404) return null
    if (surface === 'lulu') {
      return (
        <p className="m-0 text-npc-caption text-npc-lulu-t3" data-testid="project-import-card-error">
          {t('project_import.card.unavailable')}
        </p>
      )
    }
    return (
      <Card className="mb-npc-card-lg" tone="amber" data-testid="project-import-card-error">
        <CardHint>{t('project_import.card.unavailable')}</CardHint>
      </Card>
    )
  }
  if (!query.data) return null
  return <Loaded item={query.data} surface={surface} />
}

function Loaded({ item, surface }: { item: ProjectImport; surface: 'paper' | 'lulu' }) {
  const unlink = useUnlinkProjectImport(item.id)
  const [error, setError] = useState('')
  const live = (item.links ?? []).filter((link) => !link.released_at)
  const counts = tally(item)

  async function onUnlink() {
    setError('')
    try {
      await unlink.mutateAsync({ all: true })
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    }
  }

  return (
    <Card className={surface === 'lulu' ? 'mb-0 border-0 bg-transparent p-0 shadow-none' : 'mb-npc-card-lg'} data-testid="project-import-card">
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        <CardTitle className={surface === 'lulu' ? 'text-npc-lulu-t1' : undefined}>
          <T id="project_import.card.title" />
        </CardTitle>
        <Tag tone={item.status === 'approved' ? 'ok' : 'info'}>{item.status_text}</Tag>
      </div>

      <CardHint className={surface === 'lulu' ? 'mt-0 text-npc-lulu-t3' : 'mt-npc-gap-sm'}>
        <T
          id="project_import.summary.counts"
          vars={{
            verified: String(counts.verified),
            matched: String(counts.matched),
            missing: String(counts.missing),
            conflict: String(counts.conflict),
          }}
        />
      </CardHint>

      <Grades item={item} />
      <LinkedList links={live} approvedBeforeLedger={live.length === 0 && item.status === 'approved'} />
      <Renewals links={live} />

      {surface === 'paper' && item.package_digest ? (
        <dl className="mt-npc-grid-sm mb-0">
          <dt className="text-npc-caption text-npc-ink-soft">
            <T id="project_import.card.source_label" />
          </dt>
          <dd className="m-0 font-mono text-npc-caption-sm break-all">{item.package_digest}</dd>
        </dl>
      ) : null}

      {error ? <p className="m-0 mt-npc-gap-sm text-npc-body-sm text-npc-red-ink">{error}</p> : null}

      <div className="mt-npc-grid-sm flex flex-wrap items-center gap-npc-gap-sm">
        <Link
          to="/projects/import/$importId"
          params={{ importId: item.id }}
          className="text-npc-body-sm text-npc-blue-ink underline"
        >
          <T id="project_import.card.open" />
        </Link>
        <Link to="/timeline" className="text-npc-body-sm text-npc-blue-ink underline">
          <T id="project_import.card.audit" />
        </Link>
        {live.length > 0 ? (
          <Button
            variant="ghost"
            onClick={() => void onUnlink()}
            disabled={unlink.isPending}
            data-testid="project-import-unlink"
          >
            {unlink.isPending ? t('project_import.unlink.submitting') : t('project_import.action.unlink')}
          </Button>
        ) : null}
      </div>

      {live.length > 0 ? (
        <CardHint className="mt-npc-gap-sm">
          <T id="project_import.unlink.hint" />
        </CardHint>
      ) : null}
    </Card>
  )
}

/**
 * 一条账都没有时该说哪句话。
 *
 * 「一条都没接进来」与「接进来了但我没记账」是两件事，用户看完之后要做的
 * 下一个动作不同：前者去核对页勾选，后者得自己去机器与服务商那边看。
 * 合成一句「还没有东西被接进来」，对后一种就是一句假话——这次导入明明接上了东西。
 *
 * 抽成具名函数是为了让它有人守：写成 JSX 里的三元，这个区分就没有用例撑着。
 */
export function emptyLinksCopyId(approvedBeforeLedger: boolean) {
  return approvedBeforeLedger
    ? ('project_import.links.before_ledger' as const)
    : ('project_import.links.empty' as const)
}

function LinkedList({
  links,
  approvedBeforeLedger,
}: {
  links: ProjectImportLink[]
  approvedBeforeLedger: boolean
}) {
  return (
    <div className="mt-npc-grid-sm">
      <div className="text-npc-caption text-npc-ink-soft">
        <T id="project_import.links.title" />
      </div>
      {links.length === 0 ? (
        <CardHint className="mt-npc-gap-xs">
          {/* 「一条都没接进来」与「接进来了但我没记账」是两件事，
              用户看完之后要做的下一个动作不同：前者去核对页勾选，
              后者得自己去机器/服务商那边看。合成一句「还没有东西被接进来」
              是一句假话——这次导入明明接上了东西。
              来源账是 000083 才有的，这条迁移之前批准的导入一条账都没有。 */}
          <T id={emptyLinksCopyId(approvedBeforeLedger)} />
        </CardHint>
      ) : (
        <ul
          className="m-0 mt-npc-gap-xs list-none p-0 flex flex-col gap-npc-gap-xs"
          data-testid="project-import-links"
        >
          {links.map((link) => (
            <li key={link.id} className="flex flex-wrap items-center gap-npc-gap-xs">
              <span className="text-npc-body-sm">{link.title}</span>
              <Tag>
                <T id={link.kind === 'host' ? 'project_import.kind.host' : 'project_import.kind.vendor'} />
              </Tag>
              {link.created_entity ? (
                <span className="text-npc-caption-sm text-npc-ink-soft">
                  <T id="project_import.links.created_entity_conditional" />
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {/* 这句无条件说，包括一条都没连上的时候——它回答的是用户看着这张卡时
          最不放心的那个问题：「我自己连的那些会不会被它动」。 */}
      <CardHint className="mt-npc-gap-xs">
        <T id="project_import.links.pre_existing" />
      </CardHint>
    </div>
  )
}

/**
 * 续费。
 *
 * 它读的是**接进来的那几家服务商身上的到期日**，而不是资料包里那几行——
 * 包里写的是 AI 的说法，项目页上该出现的是平台自己那份事实。
 */
function Renewals({ links }: { links: ProjectImportLink[] }) {
  const vendors = links.filter((link) => link.kind === 'vendor' && link.due_at)
  if (links.filter((link) => link.kind === 'vendor').length === 0) return null
  return (
    <div className="mt-npc-grid-sm" data-testid="project-import-renewals">
      {vendors.length === 0 ? (
        <CardHint>
          <T id="project_import.renewal.none" />
        </CardHint>
      ) : (
        <ul className="m-0 list-none p-0 flex flex-col gap-npc-gap-xs">
          {vendors.map((link) => (
            <li key={link.id} className="flex flex-wrap items-center gap-npc-gap-xs text-npc-body-sm">
              <T id="project_import.renewal.due" vars={{ vendor: link.title, due: ymd(link.due_at) }} />
              {shouldRemindToday(link.due_at) ? (
                <Tag tone="warn">
                  <T id="project_import.renewal.today" />
                </Tag>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ymd(value?: string | null): string {
  if (!value) return ''
  return value.slice(0, 10)
}

function Grades({ item }: { item: ProjectImport }) {
  const claims = item.review.claims
  if (claims.length === 0) return null
  return (
    <div className="mt-npc-grid-sm" data-testid="project-import-grades">
      <div className="text-npc-caption text-npc-ink-soft">
        <T id="project_import.grades.title" />
      </div>
      <ul className="m-0 mt-npc-gap-xs list-none p-0 flex flex-col gap-npc-gap-xs">
        {claims.map((claim) => (
          <li key={claim.key} className="flex flex-wrap items-center gap-npc-gap-xs">
            <span className="text-npc-body-sm">{claim.title}</span>
            <Tag tone={evidenceTone(claim.evidence)}>{claim.evidence_text}</Tag>
          </li>
        ))}
      </ul>
    </div>
  )
}

function evidenceTone(evidence: string) {
  switch (evidence) {
    case 'platform_verified':
      return 'ok' as const
    case 'inventory_matched':
      return 'info' as const
    case 'missing':
      return 'warn' as const
    case 'conflict':
      return 'danger' as const
    default:
      return 'neutral' as const
  }
}

function tally(item: ProjectImport) {
  const counts = { verified: 0, matched: 0, missing: 0, conflict: 0 }
  for (const claim of item.review.claims) {
    if (claim.evidence === 'platform_verified') counts.verified += 1
    else if (claim.evidence === 'inventory_matched') counts.matched += 1
    else if (claim.evidence === 'missing') counts.missing += 1
    else if (claim.evidence === 'conflict') counts.conflict += 1
  }
  return counts
}

const renewalMilestones = new Set([30, 14, 7, 1, 0])

function utcDaysLeft(dueAt: string, now = new Date()): number {
  const due = new Date(dueAt)
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const end = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate())
  return Math.round((end - start) / 86_400_000)
}

function shouldRemindToday(dueAt?: string | null): boolean {
  if (!dueAt) return false
  const days = utcDaysLeft(dueAt)
  if (days < 0) return true
  return renewalMilestones.has(days)
}
